import {Nullish} from 'utility-types'
import {DragDropContext, Droppable} from 'react-beautiful-dnd'
import React, {forwardRef, MouseEventHandler, useEffect, useImperativeHandle, useMemo, useState} from 'react'
import {useParams} from 'react-router-dom'
import {partition} from 'lodash'

import {createDispatchActions, selectMethod, selectProjectData, selectTempTaskData, selectTheme} from '../../store'
import {collaboratorTypeHasRole, RIF, sortBy, useCollaboratorTypeInProject, emptyArrayToNull} from '../../lib'
import {
  EditingTask,
  ProjectScheduleSettingType,
  TempTask,
  ProjectScheduleConfigHelper,
  TaskScheduleSettingType,
  getTaskScheduleSettingType,
  getTaskContentName,
} from '../../model'
import {CancelIcon, ChevronLeftIcon, ChevronRightIcon, ParticipantTaskIcon, PlusBlueIcon} from '../../asset/image'

import {
  AddTaskPage,
  Button,
  ButtonCancel,
  ButtonReverse,
  DropdownMenu,
  FullCalendarView,
  HiddenTaskTag,
  PopupConfirmDeleteTask,
  PricingPage,
  RequiredTag,
  ScheduleTimeSlotItem,
  TaskTag,
} from '..'
import {
  TaskType,
  IProject,
  ITask,
  ProjectCollaboratorRole,
  WorkspaceCollaboratorRole,
  TaskScheduleScheduleType,
} from '../../shared/db'
import {reorderByDropResult, reorderByPartialItems} from '../../lib/ordering'
import {EventIcon} from '../../lib/svg/icon/event'
import {CalendarIcon} from '../../lib/svg/icon/calendar'
import {useToggle} from 'react-use'
import {RevertToLegacyProjectPopup} from './revert_to_legacy_project_popup'
import {RevertToLegacyProjectDeleteTaskPopup} from './revert_to_legacy_project_delete_task_popup'
import moment from 'moment'
import {Calendar} from 'react-calendar'
import styled from '@emotion/styled'
import DatePicker from 'react-datepicker'

export interface ParticipantTaskBlockProps {
  onTaskFilledChange: (filled: boolean) => void
  archiveTask: (task: TempTask) => void
  unarchiveTask: (task: TempTask) => void
  scheduleTimeSlotList: ScheduleTimeSlotItem[]
  startDate: Date
  setStartDate: (date: Date) => void
  simulationDuration: number | string
  setSimulationDuration: (duration: number | string) => void
}

interface ParticipantTaskDropdownItem {
  taskType: TaskType
  text: string
  description?: string
}

interface ParticipantTaskDropdownProps {
  items: ParticipantTaskDropdownItem[]
  text: string
  dropped: boolean
  onClickItem: (item: ParticipantTaskDropdownItem) => void
  onMouseOver: MouseEventHandler<HTMLDivElement>
  onMouseOut: MouseEventHandler<HTMLDivElement>
  onToggleDropdown?: () => void
}

interface ScheduleListItem {
  taskId: string
  taskName: string
  startDate: Date
  endDate: Date | null
  scheduleType: TaskScheduleScheduleType
}

const ParticipantTaskDropdown = (props: ParticipantTaskDropdownProps) => {
  const {text: buttonText, items, onMouseOver, onMouseOut, dropped, onClickItem, onToggleDropdown} = props
  const {color, pad} = selectTheme()
  return (
    <div
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      css={{
        display: 'flex',
        flexWrap: 'wrap',
      }}>
      <ButtonReverse
        btnPadding='medium'
        onClick={onToggleDropdown}
        children={
          <span
            css={{
              display: 'flex',
              alignItems: 'center',
              color: color.primary,
            }}>
            <img
              alt='Add Task'
              src={PlusBlueIcon}
              width='18'
              css={{
                marginRight: pad.xs,
              }}
            />
            {buttonText}
          </span>
        }
        css={{marginRight: pad.small, marginBottom: '10px'}}
      />
      <DropdownMenu
        dropped={dropped}
        parentCSS={{
          marginTop: '40px',
        }}
        options={items.map((item) => ({
          text: item.text,
          description: item.description,
          onClick: () => onClickItem(item),
        }))}
      />
    </div>
  )
}

interface DraggableTaskBlockHeaderProps {
  icon: React.ReactNode
  title: string
  displayShowCalendarBtn: boolean
  setShowFullCalendar?: (arg: boolean) => void
}

const DraggableTaskBlockHeader = (props: DraggableTaskBlockHeaderProps) => {
  const {icon, title, displayShowCalendarBtn, setShowFullCalendar} = props
  const {color, fontSize, fontWeight} = selectTheme()
  const handleClickShowFullCalendar = () => {
    if (setShowFullCalendar) {
      setShowFullCalendar(true)
    }
  }
  return (
    <div
      css={{
        display: 'flex',
        padding: 16,
        width: 'calc(100% - 24px)',
        borderRadius: 5,
        background: color.surface.default,
        alignItems: 'center',
        justifyContent: 'space-between',
        // marginRight: '24px',
      }}>
      {/* {icon} */}
      <p
        css={{
          fontWeight: fontWeight.bold,
          color: color.textIcon.title,
        }}>
        {title}
      </p>
      {displayShowCalendarBtn && (
        <button
          onClick={handleClickShowFullCalendar}
          css={{
            background: 'transparent',
            border: `1px solid ${color.border.default}`,
            borderRadius: 3,
            padding: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            cursor: 'pointer',
          }}>
          <CalendarIcon
            {...{
              width: 16,
              height: 16,
              fill: color.textIcon.secondary,
            }}
          />
          <span css={{fontSize: fontSize.h7, fontWeight: fontWeight.bold, color: color.textIcon.secondary}}>
            View Full Calendar
          </span>
        </button>
      )}
    </div>
  )
}

interface DraggableTaskBlockProps<T extends ITask | TempTask> {
  droppableId: string
  taskList: T[]
  renderTaskList: (taskList: T[]) => React.ReactNode
  renderCalendar?: () => React.ReactNode
  renderHeader?: () => React.ReactNode
}

export const DraggableTaskBlock = <T extends ITask | TempTask>(props: DraggableTaskBlockProps<T>) => {
  const {droppableId, taskList, renderHeader, renderTaskList, renderCalendar} = props

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}>
      {RIF(renderHeader, (render) => render())}
      <Droppable droppableId={droppableId}>
        {(provided) => {
          return (
            <>
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                css={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}>
                {renderCalendar && renderCalendar()}
                {renderTaskList(taskList)}
                {provided.placeholder}
              </div>
            </>
          )
        }}
      </Droppable>
    </div>
  )
}

export type ParticipantTaskBlockRefType = {
  closeTaskEditor: () => void
  editTask: (task: TempTask) => void
  addTask: (taskType: TaskType, taskScheduleSettingType: TaskScheduleSettingType) => void
}

export const ParticipantTaskBlock = forwardRef((props: ParticipantTaskBlockProps, ref) => {
  const {pad, color, fontWeight, fontSize} = selectTheme()
  const {
    doREQUEST_TASK_DELETE,
    doTEMP_TASK_DATA_UPDATE_TASK,
    doTEMP_TASK_DATA_DELETE_TASK,
    doTEMP_TASK_DATA_TASK_LIST_ORDER_UPDATE,
    doREQUEST_METHOD_TASK_INDEX_ORDER_UPDATE,
  } = createDispatchActions()

  const {
    onTaskFilledChange,
    archiveTask,
    unarchiveTask,
    scheduleTimeSlotList,
    startDate,
    setStartDate,
    simulationDuration,
    setSimulationDuration,
  } = props
  const collaboratorType = useCollaboratorTypeInProject()

  const {projectId} = useParams()
  const project = selectProjectData()[projectId as string] as IProject | Nullish
  const scheduleConfig = project?.scheduleConfig
  const methodState = selectMethod()
  const tempTaskData = selectTempTaskData()
  const isEditingLiveProject = project?.status === 'live'
  const existedTaskIdList = methodState?.taskList?.map((task) => task.id) ?? []

  const [taskList, hiddenTaskList] = useMemo(() => {
    return partition(
      (methodState?.taskList ?? []).filter(({type}) => type !== TaskType.GarminDevice).sort(sortBy('index')),
      (item) => item.enabled,
    )
  }, [methodState?.taskList])

  const tempTaskList: TempTask[] = (tempTaskData?.taskList ?? []).filter(
    (item: TempTask) => item.type !== TaskType.GarminDevice,
  )
  const [enabledTempTaskList, hiddenTempTaskList] = partition(tempTaskList, (item) => item.enabled)

  const hasTask = taskList.length > 0 || tempTaskList.length > 0 || hiddenTaskList.length > 0

  const projectScheduleSettingType = useMemo(
    () => ProjectScheduleConfigHelper.parseType(scheduleConfig),
    [scheduleConfig],
  )
  const isScheduleProject = projectScheduleSettingType === ProjectScheduleSettingType.Schedule
  const isEditingLiveScheduleProject = isScheduleProject && isEditingLiveProject

  const hasProjectUpdateRole = collaboratorTypeHasRole({
    collaboratorType,
    workspaceRole: WorkspaceCollaboratorRole.ProjectUpdate,
    projectRole: ProjectCollaboratorRole.ProjectUpdate,
  })

  const hasTaskCreateRole = collaboratorTypeHasRole({
    collaboratorType,
    workspaceRole: WorkspaceCollaboratorRole.TaskCreate,
    projectRole: ProjectCollaboratorRole.TaskCreate,
  })
  const canAddScheduleTask = hasTaskCreateRole && isScheduleProject
  const canAddEventTrackerTask =
    (hasTaskCreateRole && projectScheduleSettingType === ProjectScheduleSettingType.EventTracker) || canAddScheduleTask
  const canAddLegacyTask = hasTaskCreateRole && projectScheduleSettingType === ProjectScheduleSettingType.Legacy

  const movesenseDeviceEnable = methodState?.movesenseDeviceEnable || false
  const tempMovesenseDeviceEnable = tempTaskData?.movesenseDeviceEnable || false
  const garminStreamEnable = methodState?.garminStreamEnable || false
  const tempGarminStreamEnable = tempTaskData?.garminStreamEnable || false

  const [isAddingTask, setIsAddingTask] = useState<TaskScheduleSettingType | null>(null)
  const [removingTaskId, setRemovingTaskId] = useState('')
  const [removingTaskName, setRemovingTaskName] = useState('')
  const [showArchivedTask, setShowArchivedTask] = useState(false)
  const [displayChangePlanPage, setDisplayChangePlanPage] = useState(false)
  const [editingTask, setEditingTask] = useState<EditingTask | null>(null)
  const [taskType, setTaskType] = useState<TaskType>(TaskType.Todo)
  const [showFullCalendar, setShowFullCalendar] = useState(false)
  const [showScheduleTaskDropdown, setShowScheduleTaskDropdown] = useState(false)
  const [showEventTrackerTaskDropdown, setShowEventTrackerTaskDropdown] = useState(false)
  const [showLegacyTaskDropdown, setShowLegacyTaskDropdown] = useState(false)
  const [showSwitchToLegacyProjectPopup, toggleShowSwitchToLegacyProjectPopup] = useToggle(false)
  const [showSwitchToLegacyProjectDeleteTaskPopup, toggleShowSwitchToLegacyProjectDeleteTaskPopup] = useToggle(false)

  useImperativeHandle(ref, () => ({
    closeTaskEditor: () => {
      setIsAddingTask(null)
      setEditingTask(null)
    },

    editTask: (task: TempTask) => {
      const taskScheduleSettingType = getTaskScheduleSettingType({
        task,
        projectScheduleSettingType,
      })
      const index = tempTaskList.findIndex((tempTask) => tempTask.id === task.id)

      setIsAddingTask(taskScheduleSettingType)
      setEditingTask({
        ...task,
        index,
      })
      setTaskType(task.type)
    },

    addTask: (taskType: TaskType, taskScheduleSettingType: TaskScheduleSettingType) => {
      setEditingTask(null)
      setIsAddingTask(taskScheduleSettingType)
      setTaskType(taskType)
    },
  }))

  const createTaskDropdownItems = (type: 'eventTracker' | 'scheduled'): ParticipantTaskDropdownItem[] => {
    const descriptionMap = {
      [TaskType.Todo]: {
        scheduled: 'Participants complete tasks at scheduled times.',
        eventTracker: 'Participants log the exact time of an event.',
      },
      [TaskType.Questionnaire]: {
        scheduled: 'Participants complete a questionnaire at scheduled times.',
        eventTracker: 'Participants complete a questionnaire during an event.',
      },
      [TaskType.Timer]: {
        scheduled: 'Participants log event duration at scheduled times.',
        eventTracker: 'Participants log event duration.',
      },
    }

    const items: ParticipantTaskDropdownItem[] = [
      {taskType: TaskType.Todo, text: 'Todo Task', description: descriptionMap[TaskType.Todo][type]},
      {
        taskType: TaskType.Questionnaire,
        text: 'Questionnaire Task',
        description: descriptionMap[TaskType.Questionnaire][type],
      },
      {
        taskType: TaskType.Timer,
        text: 'Timer Task',
        description: descriptionMap[TaskType.Timer][type],
      },
    ]

    const shouldAddGarminStream = isEditingLiveProject
      ? tempGarminStreamEnable || garminStreamEnable
      : garminStreamEnable
    if (shouldAddGarminStream) {
      items.push({taskType: TaskType.StopwatchGarminStream, text: 'Garmin Stream Task'})
    }

    const shouldAddMovesenseStream = isEditingLiveProject
      ? tempMovesenseDeviceEnable || movesenseDeviceEnable
      : movesenseDeviceEnable
    if (shouldAddMovesenseStream) {
      items.push({taskType: TaskType.StopwatchMovesenseStream, text: 'Movesense Stream Task'})
    }

    return items
  }
  const scheduleTaskDropdownOptions = createTaskDropdownItems('scheduled')
  const eventTrackerTaskDropdownOptions = createTaskDropdownItems('eventTracker')
  const legacyTaskDropdownOptions = createTaskDropdownItems('eventTracker')

  useEffect(() => {
    const validTasks: (TempTask | ITask)[] = isEditingLiveProject
      ? tempTaskList.filter((item) => item.enabled)
      : taskList
    if (!validTasks.length) {
      return onTaskFilledChange(false)
    }

    let isTaskFilled = true
    if (projectScheduleSettingType !== ProjectScheduleSettingType.Legacy) {
      for (const task of validTasks) {
        let hasSchedule = undefined

        if (isEditingLiveProject) {
          hasSchedule = task.scheduleList?.length || (task as TempTask).liveEditingScheduleQueue?.length
        } else {
          hasSchedule = task.scheduleList?.length
        }
        const hasEventTracker = task.eventTracker !== undefined
        if (!hasEventTracker && !hasSchedule) {
          isTaskFilled = false
          break
        }
      }
    }
    onTaskFilledChange(isTaskFilled)
  }, [methodState.taskList, tempTaskData.taskList, projectScheduleSettingType])

  const deleteTask = () => {
    if (!isEditingLiveProject) {
      doREQUEST_TASK_DELETE({
        payload: {
          taskId: removingTaskId,
        },
      })
    } else {
      const removingTask = {...tempTaskList.find((task) => task.id === removingTaskId)}
      // hide task if it's already exist in backend
      if ((methodState.taskList ?? []).find((t) => t.id === removingTask.id)) {
        removingTask.enabled = false
        removingTask.actionType = 'hide'
        doTEMP_TASK_DATA_UPDATE_TASK(removingTask)
      } else {
        // or if not, remove it
        doTEMP_TASK_DATA_DELETE_TASK({
          taskId: removingTaskId,
        })
      }
    }
    closeConfirmRemovingTaskPopup()
  }

  const closeConfirmRemovingTaskPopup = () => {
    setRemovingTaskId('')
    setRemovingTaskName('')
  }

  const onEditingTask = <T extends ITask | TempTask>(task: T, index: number) => {
    const taskScheduleSettingType = getTaskScheduleSettingType({
      task,
      projectScheduleSettingType,
    })
    setIsAddingTask(taskScheduleSettingType)
    setEditingTask({
      ...task,
      index,
    })
    setTaskType(task.type)
  }

  const renderTaskList = <T extends ITask | TempTask>(taskList: T[]): React.ReactNode => {
    return taskList.map((task, index) => {
      return (
        <TaskTag
          {...{
            isLiveEditing: isEditingLiveProject,
            isTaskPendingToBeCreated: !existedTaskIdList.includes(task.id),
            task,
            setRemovingTaskId,
            setRemovingTaskName,
            archiveTask,
            index,
            key: task.id,
            onEditing: () => onEditingTask(task, index),
            isViewOnly: !hasTaskCreateRole,
          }}
        />
      )
    })
  }

  const CalendarContainer = styled.div`
    width: 80%;
    height: 100%;

    button {
      border: none;
      background-color: transparent;
    }

    button:disabled {
      cursor: not-allowed;
    }

    button:disabled abbr {
      color: ${color.textIcon.disabled};
    }

    .react-calendar {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .react-calendar__navigation {
      width: 100%;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .react-calendar__navigation__prev2-button {
      display: none;
    }

    .react-calendar__navigation__prev-button {
      background-image: url(${ChevronLeftIcon});
    }

    .react-calendar__navigation__next2-button {
      display: none;
    }

    .react-calendar__navigation__next-button {
      background-image: url(${ChevronRightIcon});
    }

    .react-calendar__navigation__arrow {
      width: 24px;
      height: 24px;
      border: 1px solid ${color.border.light};
      border-radius: 5px;
      color: transparent;
      background-size: 24px;
      background-repeat: no-repeat;
      background-position: center;
      cursor: pointer;
      &:hover {
        background-color: ${color.surface.hover.hoverWhiteBg};
      }
    }

    .react-calendar__navigation__label__labelText {
      font-weight: ${fontWeight.thick};
    }

    .react-calendar__viewContainer {
      flex: 1;
      display: flex;
      gap: 15px;
    }

    .react-calendar__month-view {
      flex: 1;
    }

    .calendar__century-view__decades__decade {
      font-size: ${fontSize.h9};
    }

    .react-calendar__navigation__label {
      height: 24px;
      border-radius: 5px;
      cursor: pointer;
      white-space: nowrap;
      pointer-events: none;
      &:hover {
        background-color: ${color.surface.hover.hoverWhiteBg};
      }
    }

    .react-calendar__month-view__weekdays__weekday {
      display: flex;
      justify-content: center;
    }

    .react-calendar__tile {
      position: relative;
      overflow: visible !important;
      height: 27px;
      width: 27px;
      border-radius: 50%;
      cursor: pointer;
      pointer-events: none;
      &:hover {
        background-color: ${color.surface.hover.hoverWhiteBg};
      }
    }

    .react-calendar__tile--active {
      background-color: ${color.black};
      &:hover {
        background-color: ${color.black};
      }
    }

    .react-calendar__tile--active abbr {
      color: ${color.white};
    }

    .react-calendar__month-view__weekdays__weekday {
      width: 27px;
    }

    .react-calendar__month-view__weekdays__weekday abbr {
      font-size: ${fontSize.h8};
      color: ${color.textIcon.secondary};
    }

    .react-calendar__month-view__days__day--neighboringMonth abbr {
      color: ${color.textIcon.disabled};
    }
  `

  const DatePickerContainer = styled.div`
    input {
      width: 160px;
      height: 32px;
      padding: 6px 12px;
      border-radius: 5px;
      font-size: ${fontSize.h7};
      color: ${color.textIcon.secondary};
      border: 1px solid ${color.border.default};
    }
  `

  const renderCalendar = (): React.ReactNode => {
    const isInSchedule = (date: Date) => {
      const momentDate = moment(date)
      return scheduleTimeSlotList.some(({startDate}) => momentDate.isSame(startDate, 'day'))
    }

    return (
      <div
        css={{
          width: 'calc(100% - 24px)',
          height: '320px',
          borderRadius: '5px',
          border: `1px solid ${color.border.default}`,
          boxShadow: '0 4px 12px 0 rgba(212, 212, 212, 0.25)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}>
        {showFullCalendar && (
          <FullCalendarView
            {...{
              scheduleTimeSlotList,
              taskList,
              startDate,
              setStartDate,
              simulationDuration,
              setSimulationDuration,
              setShowFullCalendar,
              isEditingLiveProject,
            }}
          />
        )}
        <div
          css={{
            width: '100%',
            display: 'flex',
            padding: '16px',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            borderBottom: `1px solid ${color.border.default}`,
          }}>
          <span css={{fontSize: fontSize.h7}}>Set a start date to simulate the calendar</span>
          <DatePickerContainer>
            <DatePicker
              {...{
                selected: startDate,
                onChange: (date) => setStartDate(date ?? new Date()),
                minDate: new Date(),
                maxDate: moment().add(1, 'year').toDate(),
              }}
            />
          </DatePickerContainer>
        </div>
        <div
          css={{
            width: '100%',
            height: '249px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px 0 8px',
            gap: '16px',
          }}>
          <CalendarContainer>
            <Calendar
              {...{
                showDoubleView: true,
                locale: 'en-US',
                minDate: startDate,
                defaultActiveStartDate: startDate,
                maxDate: moment(startDate).add(1, 'year').toDate(),
                tileContent: ({date}) => {
                  return (
                    isInSchedule(date) && (
                      <div
                        css={{
                          position: 'absolute',
                          left: 'calc(50% - 2.5px)',
                          width: '5px',
                          height: '5px',
                          borderRadius: 999,
                          backgroundColor: color.surface.blue.brand,
                        }}
                      />
                    )
                  )
                },
              }}
            />
          </CalendarContainer>
        </div>
      </div>
    )
  }

  const onTaskListReorder = (reorderedTaskList: ITask[]) => {
    if (!methodState?.taskList?.length) return

    const taskIdList = reorderByPartialItems([...methodState.taskList], reorderedTaskList, (a, b) => a.id === b.id)
      .filter((task) => {
        return task.type !== TaskType.GarminDevice
      })
      .sort((a, b) => {
        if (a.eventTracker && !b.eventTracker) return -1
        if (!a.eventTracker && b.eventTracker) return 1
        return 0
      })
      .map(({id}) => id)

    doREQUEST_METHOD_TASK_INDEX_ORDER_UPDATE({
      payload: {
        methodId: methodState.id,
        taskIdList,
      },
    })
  }

  const draggableTaskListBlock = <T extends ITask | TempTask>(taskList: T[]) => {
    const eventTrackerTaskList = taskList.filter(({eventTracker}) => eventTracker)
    const scheduleTaskList = taskList.filter((task) => {
      const hasSchedule = task.scheduleList?.length
      if (isEditingLiveProject) {
        const hasLiveEditingScheduleQueue = (task as TempTask).liveEditingScheduleQueue?.length
        return hasSchedule || hasLiveEditingScheduleQueue
      } else {
        return hasSchedule
      }
    })

    return (
      <>
        {taskList?.length ? (
          <div
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              marginTop: pad.large,
              marginBottom: '24px',
              minHeight: '120px',
            }}>
            <DraggableTaskBlock<T>
              {...{
                droppableId: 'eventTrackerTaskList',
                taskList: eventTrackerTaskList,
                renderHeader: () =>
                  eventTrackerTaskList.length ? (
                    <DraggableTaskBlockHeader
                      icon={<EventIcon fill={color.textIcon.secondary} />}
                      title='Event Tracking Task'
                      displayShowCalendarBtn={false}
                    />
                  ) : null,
                renderTaskList,
              }}
            />

            <DraggableTaskBlock<T>
              {...{
                droppableId: 'scheduleTaskList',
                taskList: scheduleTaskList,
                // Keep droppable mounted to avoid react-beautiful-dnd invariant errors.
                renderHeader: () =>
                  scheduleTaskList.length > 0 ? (
                    <div
                      css={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}>
                      <DraggableTaskBlockHeader
                        icon={<CalendarIcon fill={color.textIcon.secondary} />}
                        title='Scheduled Task'
                        displayShowCalendarBtn={true}
                        setShowFullCalendar={setShowFullCalendar}
                      />
                      {isEditingLiveProject && (
                        <p
                          css={{
                            width: 'calc(100% - 24px)',
                            borderRadius: '5px',
                            padding: '4px 8px',
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: color.tag.red.light,
                            color: color.textIcon.warning,
                            fontSize: fontSize.h7,
                          }}>
                          Note: The schedule will not reflect changes until you Save and Update this project
                        </p>
                      )}
                    </div>
                  ) : null,
                renderCalendar: () => (scheduleTaskList.length > 0 ? renderCalendar() : null),
                renderTaskList,
              }}
            />
            {RIF(
              projectScheduleSettingType === ProjectScheduleSettingType.Legacy,
              <DraggableTaskBlock<T>
                {...{
                  droppableId: 'taskList',
                  taskList,
                  renderTaskList,
                }}
              />,
            )}
          </div>
        ) : (
          <TaskListEmptyState />
        )}
      </>
    )
  }

  const onTempTaskListReorder = (newTaskList: TempTask[]) => {
    const taskIdList = reorderByPartialItems([...tempTaskData.taskList], newTaskList, (a, b) => a.id === b.id).map(
      ({id}) => id,
    )
    doTEMP_TASK_DATA_TASK_LIST_ORDER_UPDATE({taskIdList})
  }

  const onTaskDragEnd = (result: any) => {
    if (!result.destination) return
    if (result.destination.droppableId !== result.source.droppableId) return
    if (result.destination.index === result.source.index) return

    const baseTaskList = (isEditingLiveProject ? enabledTempTaskList : taskList) as Array<ITask | TempTask>

    const eventTrackerTaskList = baseTaskList.filter(({eventTracker}) => eventTracker)
    const scheduleTaskList = baseTaskList.filter((task) => {
      const hasSchedule = task.scheduleList?.length
      if (isEditingLiveProject) {
        const hasLiveEditingScheduleQueue = (task as TempTask).liveEditingScheduleQueue?.length
        return hasSchedule || hasLiveEditingScheduleQueue
      }
      return hasSchedule
    })

    switch (result.source.droppableId) {
      case 'eventTrackerTaskList': {
        const reordered = reorderByDropResult([...eventTrackerTaskList], result)
        return isEditingLiveProject
          ? onTempTaskListReorder(reordered as TempTask[])
          : onTaskListReorder(reordered as ITask[])
      }
      case 'scheduleTaskList': {
        const reordered = reorderByDropResult([...scheduleTaskList], result)
        return isEditingLiveProject
          ? onTempTaskListReorder(reordered as TempTask[])
          : onTaskListReorder(reordered as ITask[])
      }
      case 'taskList': {
        const reordered = reorderByDropResult([...baseTaskList], result)
        return isEditingLiveProject
          ? onTempTaskListReorder(reordered as TempTask[])
          : onTaskListReorder(reordered as ITask[])
      }
      default:
        return
    }
  }

  const archivedTaskListBlock = () => {
    return (
      <>
        <div css={{width: '100%', marginTop: '40px', marginBottom: '40px'}}>
          <Button
            onClick={() => {
              setShowArchivedTask((prev) => !prev)
            }}
            btnPadding='large'
            btnColor='grey_20'
            textColor={color.grey_600}
            children={
              showArchivedTask
                ? `Hide Archived tasks(${hiddenTempTaskList.length})`
                : `Show Archived tasks(${hiddenTempTaskList.length})`
            }
          />
        </div>
        {RIF(
          showArchivedTask,
          <div
            css={{
              width: '100%',
              borderRadius: '8px',
              border: `1px solid ${color.grey_100}`,
              backgroundColor: color.grey_70,
              padding: '32px',
              marginTop: '24px',
            }}>
            <p
              css={{
                fontWeight: fontWeight.bold,
                color: color.black,
                marginBottom: '12px',
              }}>
              Archived Tasks
            </p>
            <p
              css={{
                fontWeight: fontWeight.medium,
                color: color.grey_600,
                marginBottom: '24px',
              }}>
              Participants will no longer see the task, but all past completions remain visible in history and included
              in your data exports.
            </p>

            {RIF(
              emptyArrayToNull(hiddenTempTaskList.filter(({eventTracker}) => eventTracker)),
              (eventTrackerTaskList) => {
                return (
                  <>
                    <DraggableTaskBlockHeader
                      icon={<EventIcon fill={color.textIcon.secondary} />}
                      title='Event Tracking Task'
                      displayShowCalendarBtn={false}
                    />
                    <div css={{height: '12px'}} />
                    {eventTrackerTaskList.map((task, index) => {
                      return (
                        <HiddenTaskTag
                          {...{
                            task,
                            key: task.id,
                            onEditing: () => onEditingTask(task, index),
                            onUnarchive: unarchiveTask,
                          }}
                        />
                      )
                    })}
                    <div css={{height: '24px'}} />
                  </>
                )
              },
            )}

            {RIF(
              emptyArrayToNull(hiddenTempTaskList.filter(({scheduleList}) => scheduleList && scheduleList.length > 0)),
              (scheduleTaskList) => {
                return (
                  <>
                    <DraggableTaskBlockHeader
                      icon={<CalendarIcon fill={color.textIcon.secondary} />}
                      title='Scheduled Task'
                      displayShowCalendarBtn={false}
                    />
                    <div css={{height: '12px'}} />
                    {scheduleTaskList.map((task, index) => {
                      return (
                        <HiddenTaskTag
                          {...{
                            task,
                            key: task.id,
                            onEditing: () => onEditingTask(task, index),
                            onUnarchive: unarchiveTask,
                          }}
                        />
                      )
                    })}
                  </>
                )
              },
            )}

            {RIF(
              projectScheduleSettingType === ProjectScheduleSettingType.Legacy,
              <>
                {hiddenTempTaskList.map((task, index) => {
                  return (
                    <HiddenTaskTag
                      {...{
                        task,
                        key: task.id,
                        onEditing: () => onEditingTask(task, index),
                        onUnarchive: unarchiveTask,
                      }}
                    />
                  )
                })}
              </>,
            )}
          </div>,
        )}
      </>
    )
  }

  const liveProjectTaskListBlock = () => {
    return (
      <>
        {draggableTaskListBlock(enabledTempTaskList)}
        {RIF(hiddenTempTaskList.length, archivedTaskListBlock())}
      </>
    )
  }

  const TaskListBlock = () => {
    if (!isEditingLiveProject) {
      return draggableTaskListBlock(taskList)
    } else {
      return liveProjectTaskListBlock()
    }
  }

  return (
    <div
      css={{
        display: 'flex',
        background: color.white,
        borderRadius: '5px;',
        width: '55vw',
        boxShadow: '0px 4px 12px 0px #D4D4D440',
        marginBottom: pad.large,
      }}>
      <div
        css={{
          padding: pad.large,
          borderRight: `1px solid ${color.grey_100}`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          width: '26%',
        }}>
        <div>
          <div
            css={{
              width: '40px',
              height: '40px',
              background: color.background,
              borderRadius: '5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: pad.large,
            }}>
            <img alt='Participant Task' src={ParticipantTaskIcon} width='24px' />
          </div>
          <p
            css={{
              fontSize: '18px',
              fontWeight: fontWeight.thick,
              marginBottom: pad.medium,
            }}>
            Participant Tasks
          </p>
          <p css={{lineHeight: '19px', fontWeight: fontWeight.regular, marginBottom: pad.medium}}>
            Create tasks for your participants to complete throughout your study.
          </p>
          {RIF(
            !isEditingLiveProject &&
              hasProjectUpdateRole &&
              projectScheduleSettingType !== ProjectScheduleSettingType.Legacy,
            <button
              type='button'
              onClick={() => {
                if (showSwitchToLegacyProjectPopup) {
                  return toggleShowSwitchToLegacyProjectPopup(false)
                }
                if (showSwitchToLegacyProjectDeleteTaskPopup) {
                  return toggleShowSwitchToLegacyProjectDeleteTaskPopup(false)
                }
                if (hasTask) {
                  return toggleShowSwitchToLegacyProjectDeleteTaskPopup(true)
                } else {
                  return toggleShowSwitchToLegacyProjectPopup(true)
                }
              }}
              css={{backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: 0}}>
              <span
                css={{
                  color: color.textIcon.secondary,
                  fontSize: fontSize.h7,
                  fontWeight: fontWeight.medium,
                  textDecoration: 'underline',
                  textAlign: 'start',
                }}>
                I want to switch to legacy project
              </span>
            </button>,
          )}
        </div>
      </div>
      <div
        css={{
          padding: 30,
          width: '74%',
        }}>
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            marginBottom: '14px',
          }}>
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
            }}>
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
              <p
                css={{
                  fontSize: '14px',
                  fontWeight: fontWeight.medium,
                }}>
                Select the type of task you want to add:
              </p>
              <RequiredTag style={{color: color.state.warning, background: color.tag.red.light}} />
            </div>
          </div>
        </div>
        <div
          css={{
            display: 'flex',
            alignItems: 'flex-start',
            marginBottom: '6px',
          }}>
          {RIF(
            canAddEventTrackerTask || canAddScheduleTask,
            <div
              css={{
                display: 'flex',
                alignItems: 'start',
                gap: '12px',
              }}>
              {RIF(
                canAddEventTrackerTask,
                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'start',
                    gap: '12px',
                  }}>
                  <ParticipantTaskDropdown
                    {...{
                      items: eventTrackerTaskDropdownOptions,
                      text: 'Add Event Tracker',
                      dropped: showEventTrackerTaskDropdown,
                      onToggleDropdown: () => setShowEventTrackerTaskDropdown((prev) => !prev),
                      onClickItem: ({taskType}) => {
                        setShowEventTrackerTaskDropdown(false)
                        setIsAddingTask(TaskScheduleSettingType.EventTracker)
                        setTaskType(taskType)
                      },
                      onMouseOver: () => setShowEventTrackerTaskDropdown(true),
                      onMouseOut: () => setShowEventTrackerTaskDropdown(false),
                    }}
                  />
                  <p
                    css={{
                      color: color.textIcon.secondary,
                      fontSize: fontSize.h7,
                      fontWeight: fontWeight.medium,
                    }}>
                    Used to track event-based occurrences marked by participants. These tasks are persistent throughout
                    the study.
                  </p>
                </div>,
              )}
              {/* divider */}
              {RIF(
                canAddScheduleTask && canAddEventTrackerTask,
                <div
                  css={{
                    width: '1px',
                    height: '128px',
                    backgroundColor: color.border.default,
                    marginRight: '12px',
                  }}
                />,
              )}
              {RIF(
                canAddScheduleTask,
                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'start',
                    gap: '12px',
                  }}>
                  <ParticipantTaskDropdown
                    {...{
                      items: scheduleTaskDropdownOptions,
                      text: 'Add Scheduled Task',
                      dropped: showScheduleTaskDropdown,
                      onToggleDropdown: () => setShowScheduleTaskDropdown((prev) => !prev),
                      onClickItem: ({taskType}) => {
                        setShowScheduleTaskDropdown(false)
                        setIsAddingTask(TaskScheduleSettingType.Schedule)
                        setTaskType(taskType)
                      },
                      onMouseOver: () => setShowScheduleTaskDropdown(true),
                      onMouseOut: () => setShowScheduleTaskDropdown(false),
                    }}
                  />
                  <p
                    css={{
                      color: color.textIcon.secondary,
                      fontSize: fontSize.h7,
                      fontWeight: fontWeight.medium,
                    }}>
                    Used for scheduled tasks that appear at set times. These tasks are hidden until available and can
                    expire.
                  </p>
                </div>,
              )}
            </div>,
          )}
          {RIF(
            canAddLegacyTask,
            <ParticipantTaskDropdown
              {...{
                items: legacyTaskDropdownOptions,
                text: 'Add Task',
                dropped: showLegacyTaskDropdown,
                onToggleDropdown: () => setShowLegacyTaskDropdown((prev) => !prev),
                onClickItem: ({taskType}) => {
                  setShowLegacyTaskDropdown(false)
                  setIsAddingTask(TaskScheduleSettingType.Legacy)
                  setTaskType(taskType)
                },
                onMouseOver: () => setShowLegacyTaskDropdown(true),
                onMouseOut: () => setShowLegacyTaskDropdown(false),
              }}
            />,
          )}
        </div>
        <DragDropContext onDragEnd={onTaskDragEnd}>
          <TaskListBlock />
        </DragDropContext>
      </div>
      {RIF(isAddingTask, (taskScheduleSettingType) => (
        <AddTaskPage
          {...{
            isEditing: editingTask,
            taskType,
            closePopup: () => {
              setIsAddingTask(null)
              setEditingTask(null)
            },
            setDisplayChangePlanPage,
            archiveTask,
            unarchiveTask,
            taskScheduleSettingType,
            projectScheduleConfig: scheduleConfig,
          }}
        />
      ))}
      {RIF(
        displayChangePlanPage,
        <PricingPage
          {...{
            setDisplayChangePlanPage,
          }}
        />,
      )}
      {/* confirm delete task popup */}
      {RIF(
        !!removingTaskId,
        <PopupConfirmDeleteTask
          {...{
            deleteAction: deleteTask,
            closeAction: closeConfirmRemovingTaskPopup,
            taskName: removingTaskName,
          }}
        />,
      )}
      {/* revert to legacy project popup */}
      {RIF(
        showSwitchToLegacyProjectPopup,
        RIF(projectId, (projectId) => (
          <RevertToLegacyProjectPopup
            {...{
              projectId,
              onClose: () => toggleShowSwitchToLegacyProjectPopup(false),
              onFinish: () => toggleShowSwitchToLegacyProjectPopup(false),
            }}
          />
        )),
      )}
      {/* revert to legacy project delete task popup */}
      {RIF(
        showSwitchToLegacyProjectDeleteTaskPopup,
        <RevertToLegacyProjectDeleteTaskPopup
          {...{
            onClose: () => toggleShowSwitchToLegacyProjectDeleteTaskPopup(false),
            onConfirm: () => toggleShowSwitchToLegacyProjectDeleteTaskPopup(false),
          }}
        />,
      )}
    </div>
  )
})

const TaskListEmptyState = () => {
  const {color, pad, fontSize} = selectTheme()
  return (
    <div
      css={{
        borderRadius: '5px',
        border: `1px dashed ${color.grey_400}`,
        height: '296px',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: pad.large,
      }}>
      <p
        css={{
          fontSize: fontSize.h4,
          color: color.grey_400,
        }}>
        You don't have any tasks set up yet
      </p>
    </div>
  )
}
